import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Banner, Button, Card, Checkbox, Field, Mono, PageBody, PageHeader } from "../ui/Field";

type S = {
  pwaEnabled: boolean;
  metaTitleTemplate: string;
  metaDescriptionFallback: string;
  gtmContainerId: string;
  headerCta: { text: string; link: string };
  cspOverrides: { scriptSrc: string[]; styleSrc: string[]; connectSrc: string[]; imgSrc: string[]; frameSrc: string[] };
};

export default function Settings() {
  const { slug } = useParams();
  const [s, setS] = useState<S | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.get<S>(`/api/sites/${slug}/settings`).then(setS).catch((e) => setErr((e as Error).message));
  }, [slug]);

  async function save() {
    if (!s || !slug) return;
    setOk(false); setErr(null);
    try { await api.put(`/api/sites/${slug}/settings`, s); setOk(true); }
    catch (e) { setErr((e as Error).message); }
  }

  if (!s) return <PageBody><div className="h-32 bg-[var(--color-paper-2)] animate-pulse rounded" /></PageBody>;

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 10 · Configuration"
        title={<>Site <em>settings</em></>}
        description="SEO defaults, analytics, PWA, header CTA, and Content Security Policy overrides. Applied on the next publish."
        actions={<Button onClick={save}>Save settings</Button>}
      />
      {err && <Banner kind="error">{err}</Banner>}
      {ok && <Banner kind="success">Saved.</Banner>}

      <Card number="S1" title="SEO defaults">
        <Field label="Meta title template" value={s.metaTitleTemplate} onChange={(e) => setS({ ...s, metaTitleTemplate: e.target.value })} help="Use %title% as placeholder for the page title. e.g. '%title% | Acme Inc.'" />
        <Field label="Meta description fallback" value={s.metaDescriptionFallback} onChange={(e) => setS({ ...s, metaDescriptionFallback: e.target.value })} help="Used when a page doesn't specify its own description." />
      </Card>

      <Card number="S2" title="Analytics" className="mt-6">
        <Field label="GTM Container ID" value={s.gtmContainerId} onChange={(e) => setS({ ...s, gtmContainerId: e.target.value })} placeholder="GTM-XXXXXXX" help="Google Tag Manager. Leave blank to disable. (Phase 2: also offered as a per-page block.)" />
      </Card>

      <Card number="S3" title="Progressive web app" className="mt-6">
        <Checkbox
          checked={s.pwaEnabled}
          onChange={(e) => setS({ ...s, pwaEnabled: e.target.checked })}
          label={<>Generate <Mono>manifest.webmanifest</Mono> and service worker on every build</>}
          help="Enables offline shell and add-to-homescreen prompts."
        />
      </Card>

      <Card number="S4" title="Header CTA defaults" className="mt-6">
        <Field label="Text" value={s.headerCta.text} onChange={(e) => setS({ ...s, headerCta: { ...s.headerCta, text: e.target.value } })} />
        <Field label="Link" value={s.headerCta.link} onChange={(e) => setS({ ...s, headerCta: { ...s.headerCta, link: e.target.value } })} />
      </Card>

      <Card
        number="S5"
        title="Content Security Policy"
        description="Allow extra origins. Comma-separated. Always appended to the default 'self'."
        className="mt-6"
      >
        {(["scriptSrc", "styleSrc", "connectSrc", "imgSrc", "frameSrc"] as const).map((k) => (
          <Field
            key={k}
            label={k}
            value={(s.cspOverrides[k] ?? []).join(", ")}
            onChange={(e) => setS({ ...s, cspOverrides: { ...s.cspOverrides, [k]: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) } })}
            placeholder="https://example.com, https://cdn.example.com"
          />
        ))}
      </Card>

      <div className="mt-6 flex justify-end">
        <Button onClick={save}>Save settings</Button>
      </div>
    </PageBody>
  );
}
